import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { User } from '../../../models/Entities/Authentication/User';
import { FontIcon, IconButton, MessageBarType, PrimaryButton, SearchBox, Separator, Spinner } from '@fluentui/react';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import { connect } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';
import ProtocolContractFormComponent from '../Protocols/ProtocolContractForm/ProtocolContractFormComponent';
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';

////i18Next
import { useTranslation } from 'react-i18next';

// functions
import * as ComponentFunctions from './ContractList.Functions';

// Styles
import './ContractList.Styles.css';
import moment from 'moment';
import ContractCloneFormComponent from '../ContractCloneForm/ContractCloneForm.Component';


// ─── Permission helpers (defined outside for stable references) ───────────────

interface ContractCardProps {
  item: Contract;
  users: User[];
  t: (key: string) => string;
  onSelected: (c: Contract) => void;
  onEdit: (c: Contract) => void;
  onClone: (c: Contract) => void;
  onDelete: (c: Contract) => void;
  isDeleting: boolean;
  failOnDeleting: boolean;
  error: any;
}

const ContractCard = ({ item, users, t, onSelected, onEdit, onClone, onDelete, isDeleting, failOnDeleting, error }: ContractCardProps) => {
  const noAccess = !item.userCanView && !item.userCanModify;
  const readOnly = item.userCanView && !item.userCanModify;

  if (noAccess) {
    return (
      <div className="contract-card contract-card--locked">
        <div className="contract-card__accent contract-card__accent--locked" />
        <div className="contract-card__body">
          <div className="contract-card__header">
            <span className="contract-card__number contract-card__number--locked">
              #{item.id}
            </span>
            <FontIcon iconName="Lock" className="contract-card__lock-icon" />
          </div>
          <div className="contract-card__title-block">
            <h3 className="contract-card__title contract-card__title--locked">{item.contractNumber.toUpperCase()}</h3>
          </div>
          <p className="contract-card__no-access-label">Sin acceso</p>
        </div>
      </div>
    );
  }

  const statusClass = ComponentFunctions.GetContractStatusClass(item.contractStatus);

  return (
    <button
      type="button"
      className={`contract-card contract-card--${statusClass.toLowerCase()}${item.contractVerified ? ' contract-card--reviewed' : ''}`}
      onClick={() => onSelected(item)}
    >
      <div className={`contract-card__accent contract-card__accent--${statusClass.toLowerCase()}`} />
      <div className="contract-card__body">

        <div className="contract-card__header">
          <div className="contract-card__id-group">
            <span className="contract-card__number">#{item.id}</span>
            {readOnly && (
              <span className="contract-card__badge contract-card__badge--readonly">
                <FontIcon iconName="View" />&nbsp;Solo lectura
              </span>
            )}
            <span className={`contract-card__badge contract-card__badge--status contract-card__badge--${statusClass.toLowerCase()}`}>
              {ComponentFunctions.GetContractStatusDescripcion(item.contractStatus)}
            </span>
            {/* Shown in both states on purpose: "not reviewed yet" is as much a
                fact about the contract as "reviewed" is. */}
            <span className={item.contractVerified
              ? 'contract-card__badge contract-card__badge--reviewed'
              : 'contract-card__badge contract-card__badge--unreviewed'}>
              <FontIcon iconName={item.contractVerified ? 'SkypeCircleCheck' : 'AlertSolid'} />
              &nbsp;{item.contractVerified
                ? t('contractHeader.verification.verified')
                : t('contractHeader.verification.notVerified')}
            </span>
          </div>

          {!readOnly && (
            <div
              className="contract-card__actions"
              role="toolbar"
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
            >
              <IconButton
                iconProps={{ iconName: 'Edit' }}
                title={t('contractList.contentTable.information')}
                ariaLabel="Editar"
                onClick={() => onEdit(item)}
              />
              <IconButton
                iconProps={{ iconName: 'ChromeRestore' }}
                title="Clonar contrato"
                ariaLabel="Clonar"
                onClick={() => onClone(item)}
              />
              <ConfirmationButtonComponent
                ButtonId={"deleteContract" + item.id}
                ButtonIconName={"Delete"}
                ButtonText={""}
                ButtonCssClass={""}
                ConfirmationButtonText={t('contractList.contentTable.confirmationButtonDelete.confirm')}
                ConfirmationButtonCssClass={"deleteButton"}
                CancelButtonText={t('contractList.contentTable.confirmationButtonDelete.cancel')}
                CancelButtonCssClass={""}
                Title={t('contractList.contentTable.confirmationButtonDelete.deleteContract')}
                Description={""}
                ElementTextHeader={t('contractList.contentTable.confirmationButtonDelete.deleteContractInformation') + item.contractNumber}
                ElementTextInformation={t('contractList.contentTable.confirmationButtonDelete.operationInfo')}
                onConfirmation={() => onDelete(item)}
                onCancel={() => undefined}
                isOperationExecuting={isDeleting}
                isOperationExecutedWithError={failOnDeleting}
                OperationError={error}
              />
            </div>
          )}
        </div>

        <div className="contract-card__title-block">
          <h3 className="contract-card__title">{item.contractNumber.toUpperCase()}</h3>
        </div>

        {item.contractDescription && (
          <p className="contract-card__description">{item.contractDescription}</p>
        )}

        <div className="contract-card__meta">
          {(item.practitioners?.length > 0 || item.protocol?.length > 0) && (
            <div className="contract-card__links">
              {item.practitioners?.length > 0 && (
                <span className="contract-card__tag">
                  <FontIcon iconName="UserFollowed" />&nbsp;{item.practitioners.length}
                </span>
              )}
              {item.protocol?.length > 0 && (
                <span className="contract-card__tag">
                  <FontIcon iconName="TestUserSolid" />&nbsp;{item.protocol.length}
                </span>
              )}
            </div>
          )}

          <div className="contract-card__dates">
            <FontIcon iconName="Calendar" />
            <span>
              {item.contractStartDate ? moment(item.contractStartDate).format('DD/MM/YYYY') : t('contractList.contractStartDate.notDefined')}
              &nbsp;—&nbsp;
              {item.contractEndDate ? moment(item.contractEndDate).format('DD/MM/YYYY') : t('contractList.contractEndDate.notDefined')}
            </span>
          </div>
        </div>

        {item.userPermissions?.length > 0 && (
          <div className="contract-card__permissions">
            <div className="contract-card__permissions-header">
              <FontIcon iconName="Permissions" className="contract-card__permissions-icon" />
              <span className="contract-card__permissions-title">Permisos de acceso</span>
            </div>
            <ul className="contract-card__permissions-list">
              {item.userPermissions.map(perm => {
                const user = users.find(u => u.userId === perm.userId);
                const displayName = user?.fullName || user?.userName || perm.userId;
                const initials = displayName
                  .split(' ')
                  .map((n: string) => n[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase();
                return (
                  <li key={perm.userId} className="contract-card__permission-row">
                    <span className="contract-card__permission-avatar" aria-hidden="true">
                      {user?.photo
                        ? <img src={user.photo} alt={displayName} className="contract-card__permission-avatar-img" />
                        : initials
                      }
                    </span>
                    <span className="contract-card__permission-name" title={user?.email}>{displayName}</span>
                    <span className="contract-card__permission-badges">
                      {perm.canWrite ? (
                        <span className="contract-card__perm-badge contract-card__perm-badge--write">
                          <FontIcon iconName="Edit" />&nbsp;Escritura
                        </span>
                      ) : perm.canRead ? (
                        <span className="contract-card__perm-badge contract-card__perm-badge--read">
                          <FontIcon iconName="View" />&nbsp;Lectura
                        </span>
                      ) : (
                        <span className="contract-card__perm-badge contract-card__perm-badge--none">
                          <FontIcon iconName="BlockedSite" />&nbsp;Sin acceso
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

      </div>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ContractListComponent = (props: ComponentFunctions.Props) => {

  const [selectedContract, setSelectedContract] = useState<Contract | undefined>(undefined);

  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [isCloneFormOpen, { setTrue: openCloneFormPanel, setFalse: dismissCloneFormPanel }] = useBoolean(false);

  const [searchText, setSearchText] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<number, boolean>>({});

  const [t] = useTranslation();

  useEffect(() => {
    props.GetAllContracts();
  }, []);

  const handleClosePanel = () => {
    setSelectedContract(undefined);
    dismissPanel();
  };

  const handleCloseCloneFormPanel = () => {
    setSelectedContract(undefined);
    dismissCloneFormPanel();
  };

  const onSelectedContract = (contract: Contract) => {
    if (props.SelectedEvent) props.SelectedEvent(contract);
  };

  const onViewContractEvent = (contract: Contract) => {
    setSelectedContract(contract);
    openPanel();
  };

  const onRequestCloneContract = (contract: Contract) => {
    setSelectedContract(contract);
    openCloneFormPanel();
  };

  const onRequestDeleteContract = (contract: Contract) => {
    props.DeleteContract(contract.id);
  };

  const onSearch = (newValue: string) => setSearchText(newValue);

  const toggleGroup = (status: number) =>
    setCollapsedGroups(prev => ({ ...prev, [status]: !prev[status] }));

  // ─── Derived data ───
  const filteredContracts = (props.Contracts ?? []).filter((item: Contract) =>
    item.contractNumber.toLowerCase().includes(searchText.toLowerCase()) ||
    item.contractDescription?.toLowerCase().includes(searchText.toLowerCase())
  );

  const groupedContracts = (() => {
    const map = new Map<number, { status: number; label: string; items: Contract[] }>();
    filteredContracts.forEach((item: Contract) => {
      if (!map.has(item.contractStatus)) {
        map.set(item.contractStatus, {
          status: item.contractStatus,
          label: ComponentFunctions.GetContractStatusDescripcion(item.contractStatus),
          items: []
        });
      }
      map.get(item.contractStatus)!.items.push(item);
    });
    // Active first, then rest sorted by status number
    return Array.from(map.values()).sort((a, b) => {
      if (a.status === 1) return -1;
      if (b.status === 1) return 1;
      return a.status - b.status;
    });
  })();

  // ─── Sub-sections ───

  const Controls = props.ShowControls && (
    <>
      <div className="contracts-page-header">
        <div className="contracts-page-header__title-group">
          <FontIcon iconName="EntitlementRedemption" className="contracts-page-header__icon" />
          <div>
            <h2 className="contracts-page-header__title">{t('contractList.title') || 'Contratos'}</h2>
            {props.isLoadingSuccessfully && (
              <span className="contracts-page-header__count">
                {filteredContracts.length} de {props.Contracts?.length ?? 0}
              </span>
            )}
          </div>
        </div>
        <div className="contracts-page-header__actions">
          <SearchBox
            placeholder={t('contractList.searchBox.search')}
            underlined={false}
            onChange={(ev, newValue) => { if (newValue !== undefined) onSearch(newValue); }}
            onClear={() => onSearch('')}
            className="contracts-page-header__search"
          />
          <PrimaryButton
            text={t('contractList.componentControls.addContract')}
            iconProps={{ iconName: 'Add' }}
            onClick={openPanel}
          />
        </div>
      </div>
      <Separator />
    </>
  );

  const PrincipalErrorMessage = props.failOnLoading && (
    <MessageComponent
      message={t('contractList.errorMessage.contractError')}
      subMessage={props.error?.ErrorMessage || ''}
      type={MessageBarType.error}
      itemList={props.error?.Errors || []}
    />
  );

  const Loading = props.isLoading && (
    <div className="contracts-list__loading">
      <Spinner label={t('contractList.loading.loadingContracts')} ariaLive="assertive" labelPosition="top" />
    </div>
  );

  const ContractGroups = props.isLoadingSuccessfully && (
    filteredContracts.length > 0
      ? (
        <div className="contracts-groups">
          {groupedContracts.map(group => {
            const collapsed = !!collapsedGroups[group.status];
            const statusClass = ComponentFunctions.GetContractStatusClass(group.status).toLowerCase();
            return (
              <div key={group.status} className={`contracts-group${collapsed ? ' contracts-group--collapsed' : ''}`}>
                <button className="contracts-group__header" onClick={() => toggleGroup(group.status)}>
                  <div className="contracts-group__header-left">
                    <FontIcon iconName="ChevronDown" className="contracts-group__chevron" />
                    <span className={`contracts-group__status-dot contracts-group__status-dot--${statusClass}`} />
                    <span className="contracts-group__title">{group.label}</span>
                    <span className="contracts-group__count">{group.items.length}</span>
                  </div>
                </button>
                <div className="contracts-group__body">
                  <div className="contracts-list__grid">
                    {group.items.map(item => (
                      <div key={item.id} className="contract-card-wrapper">
                        <ContractCard
                          item={item}
                          users={props.Users ?? []}
                          t={t}
                          onSelected={onSelectedContract}
                          onEdit={onViewContractEvent}
                          onClone={onRequestCloneContract}
                          onDelete={onRequestDeleteContract}
                          isDeleting={props.isDeleting}
                          failOnDeleting={props.failOnDeleting}
                          error={props.error}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )
      : (
        <div className="contracts-list__empty">
          <img
            src={require('../../../assets/img/empty_state.gif')}
            alt="Sin contratos"
            style={{ width: '8em', height: '8em', marginBottom: '1em' }}
          />
          <span>{t('contractList.noRegistries.registeredContracts')}</span>
        </div>
      )
  );

  const Forms = (
    <>
      <ProtocolContractFormComponent
        isOpen={isOpen}
        Contract={selectedContract}
        dismissPanel={handleClosePanel}
        openPanel={openPanel}
        mode=''
        RefererId={undefined}
      />
      {selectedContract && (
        <ContractCloneFormComponent
          isOpen={isCloneFormOpen}
          ContractId={selectedContract.id}
          dismissPanel={handleCloseCloneFormPanel}
          openPanel={openCloneFormPanel}
        />
      )}
    </>
  );

  return (
    <>
      {Controls}
      {PrincipalErrorMessage}
      {ContractGroups}
      {Loading}
      {Forms}
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ContractListComponent as any);

